import {
  Target,
  Mouse,
  Shield,
  Eye,
  Microscope,
  Clock,
} from "lucide-react";

export const DERATIZZAZIONE_PAGE = {
  HEADER: {
    TITLE: "Derattizzazione Professionale",
    SUBTITLE:
      "Controllo e Eliminazione Definitiva di Topi e Ratti",
    DESCRIPTION:
      "Servizio completo di derattizzazione con sistemi integrati, monitoraggio costante e garanzia di risultato per privati, aziende e strutture pubbliche.",
    BACK_BUTTON: "Torna alla Home",
  },

  OVERVIEW: {
    TITLE: "Perché Scegliere la Nostra derattizzazione?",
    DESCRIPTION:
      "La presenza di roditori rappresenta un serio rischio per la salute e la sicurezza. I nostri interventi di derattizzazione utilizzano metodologie scientifiche avanzate, prodotti certificati e tecniche di esclusione fisica per garantire risultati duraturi.",
    FEATURES: [
      {
        title: "Approccio Scientifico",
        description:
          "Analisi comportamentale dei roditori e strategie mirate per ogni specie",
      },
      {
        title: "Sicurezza Garantita",
        description:
          "Prodotti sicuri per bambini e animali domestici, applicazioni controllate",
      },
      {
        title: "Prevenzione Duratura",
        description:
          "Interventi di esclusione fisica per prevenire future infestazioni",
      },
    ],
    BUTTONS: {
      PRIMARY: "Preventivo Gratuito",
      SECONDARY: "Chiama: 123 456 7890",
    },
    FORM: {
      TITLE: "Richiedi Preventivo derattizzazione",
      SUBTITLE:
        "Sopralluogo gratuito e preventivo personalizzato entro 24 ore",
    },
    EMERGENCY: {
      TITLE: "Emergenza Roditori!",
      DESCRIPTION:
        "Hai avvistato topi o ratti? Non aspettare! I roditori si moltiplicano rapidamente e possono causare gravi danni strutturali e sanitari.",
      BUTTON: "Richiedi Intervento Urgente",
      FORM_TITLE: "Richiedi Intervento Urgente derattizzazione",
      FORM_SUBTITLE:
        "Intervento rapido per situazioni di emergenza - Risposta entro 2 ore",
    },
  },

  MAIN_IMAGE: {
    URL: "https://images.unsplash.com/photo-1628258334105-2a0b3d6efee1?w=600&h=400&fit=crop",
    ALT: "Tecnico specializzato in derattizzazione al lavoro",
  },

  METHODS: {
    TITLE: "I Nostri Sistemi di derattizzazione",
    SUBTITLE:
      "Utilizziamo un approccio integrato che combina diverse metodologie per garantire l'eliminazione completa e la prevenzione duratura.",
    TECHNIQUES: [
      {
        id: "baiting-stations",
        title: "Stazioni di Esca",
        description:
          "Sistemi di controllo sicuri con esche rodenticide in contenitori a prova di bambino",
        icon: Target,
        features: [
          "Contenitori antimanomissione",
          "Esche fresche e attrattive",
          "Sicurezza per non-target",
          "Controllo dell'efficacia",
        ],
        applications: [
          "Ambienti interni",
          "Aree sensibili",
          "Controllo continuo",
        ],
      },
      {
        id: "trapping-systems",
        title: "Sistemi di Cattura",
        description:
          "Trappole professionali per la cattura viva o letale dei roditori",
        icon: Mouse,
        features: [
          "Trappole multi-cattura",
          "Sistemi elettronici",
          "Controllo remoto",
          "Cattura selettiva",
        ],
        applications: [
          "Aree alimentari",
          "Controllo mirato",
          "Monitoraggio",
        ],
      },
      {
        id: "exclusion",
        title: "Esclusione Fisica",
        description:
          "Sigillatura di punti di accesso e modifica dell'habitat per prevenire l'infestazione",
        icon: Shield,
        features: [
          "Analisi punti di accesso",
          "Materiali resistenti",
          "Modifica ambientale",
          "Prevenzione duratura",
        ],
        applications: [
          "Prevenzione",
          "Controllo integrato",
          "Soluzione definitiva",
        ],
      },
      {
        id: "monitoring",
        title: "Monitoraggio Continuo",
        description:
          "Sistemi di controllo permanente con report periodici e interventi mirati",
        icon: Eye,
        features: [
          "Stazioni di monitoraggio",
          "Report periodici",
          "Interventi programmati",
          "Analisi dei dati",
        ],
        applications: [
          "Controllo qualità",
          "Prevenzione",
          "Piani HACCP",
        ],
      },
    ],
  },

  RODENT_TYPES: {
    TITLE: "Tipi di Roditori che Trattiamo",
    SUBTITLE:
      "Ogni specie di roditore richiede strategie specifiche. Identifichiamo con precisione il tipo di infestante per applicare il trattamento più efficace.",
    SPECIES: [
      {
        name: "Rattus Norvegicus (Ratto delle Fogne)",
        description:
          "Grande, aggressivo, vive in ambienti umidi e scava gallerie",
        characteristics: [
          "Lunghezza: 40-50cm",
          "Peso: 200-500g",
          "Coda più corta del corpo",
          "Colore marrone-grigio",
        ],
        habitat: "Fognature, cantine, magazzini, aree umide",
        risks:
          "Malattie gravi, danni strutturali, contaminazione alimentare",
        image:
          "figma:asset/771495d791b1aaa1b0cb39e619ce2c0ff9844f32.png",
      },
      {
        name: "Rattus Rattus (Ratto dei Tetti)",
        description:
          "Agile scalatore, preferisce ambienti elevati e asciutti",
        characteristics: [
          "Lunghezza: 35-45cm",
          "Peso: 150-250g",
          "Coda più lunga del corpo",
          "Colore nero-grigio",
        ],
        habitat: "Soffitti, sottotetti, piani alti, alberi",
        risks:
          "Cortocircuiti elettrici, contaminazione, danni alle merci",
        image:
          "figma:asset/11666e0133e997f7fabf674bfd16455cd55e7017.png",
      },
      {
        name: "Mus Domesticus (Topo Domestico)",
        description:
          "Piccolo ma prolifico, si adatta facilmente agli ambienti umani",
        characteristics: [
          "Lunghezza: 15-20cm",
          "Peso: 15-25g",
          "Coda lunga quanto il corpo",
          "Colore grigio-marrone",
        ],
        habitat:
          "Case, uffici, negozi, luoghi caldi e riparati",
        risks:
          "Rapida proliferazione, contaminazione, allergie",
        image:
          "figma:asset/26c67f73bec987ffe699feab376b4fdc5a3e40b2.png",
      },
    ],
  },

  PROCESS: {
    TITLE: "Il Nostro Processo in 6 Fasi",
    SUBTITLE:
      "Un approccio sistematico per garantire risultati efficaci e duraturi",
    PHASES: [
      {
        step: "1",
        title: "Ispezione",
        description:
          "Sopralluogo gratuito per identificare tipo di roditore, grado di infestazione e punti critici",
        icon: Target,
        color: "bg-blue-100 text-blue-600",
      },
      {
        step: "2",
        title: "Strategia",
        description:
          "Sviluppo di un piano di intervento personalizzato con cronogramma e metodi specifici",
        icon: Microscope,
        color: "bg-green-100 text-green-600",
      },
      {
        step: "3",
        title: "Intervento",
        description:
          "Applicazione delle tecniche selezionate con installazione sistemi di controllo e monitoraggio",
        icon: Shield,
        color: "bg-purple-100 text-purple-600",
      },
      {
        step: "4",
        title: "Monitoraggio",
        description:
          "Controlli periodici per verificare l'efficacia e sostituire le esche",
        icon: Eye,
        color: "bg-orange-100 text-orange-600",
      },
      {
        step: "5",
        title: "Esclusione",
        description:
          "Sigillatura dei punti di accesso per prevenire nuove infestazioni",
        icon: Shield,
        color: "bg-red-100 text-red-600",
      },
      {
        step: "6",
        title: "Follow-up",
        description:
          "Controlli periodici, manutenzione sistemi e report dettagliati sui risultati ottenuti",
        icon: Clock,
        color: "bg-gray-100 text-gray-600",
      },
    ],
  },

  CTA_SECTION: {
    TITLE: "Pronto per Risolvere il Problema Roditori?",
    DESCRIPTION:
      "Non lasciare che topi e ratti danneggino la tua proprietà. Contattaci subito per un sopralluogo gratuito e ricevi una soluzione definitiva e garantita.",
    PRIMARY_BUTTON: "Preventivo Gratuito",
    FORM_TITLE: "Richiedi Preventivo derattizzazione",
    FORM_SUBTITLE:
      "Sopralluogo gratuito e preventivo personalizzato entro 24 ore",
    DEFAULT_SERVICE: "derattizzazione",
  },
} as const;